// Monitoreo de LightData → Current (adaptador TRANSITORIO, ver
// server/integrations/lightdata). Responde lo que pregunta el que lo mira:
// ¿está prendido?, ¿está corriendo?, ¿cuánto entró?, ¿qué quedó trabado y
// cómo lo destrabo? Lo más común que traba es un cliente de LightData sin
// tienda: se puede asociar desde acá (si se tiene finance_access, el mismo
// permiso que la pestaña Legacy de Tiendas).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import LightdataReconciliation from "./LightdataReconciliation";

type SyncState = { name: string; mode: string; since: string; last_run_at: string | null; last_result: any } | null;
export type Status = {
  scan_import: "on" | "off";
  sync_mode: "off" | "shadow" | "on";
  oauth_wait_seconds: number;
  sync_interval_seconds: number;
  status_interval_seconds: number;
  sync_lookback_days?: number;
  state: SyncState;
  totals: Record<string, number>;
  oldest_pending_at: string | null;
  stale_pending?: number;
  recent_errors?: number;
  status_pass: { last_run_at: string | null; last_result: { seen?: number; changed?: number; closed?: number; errors?: number; settled?: number; error?: string } | null } | null;
  imported_24h?: { scan: number; sync: number };
  open_exceptions: Record<string, number>;
};
type Exception = {
  id: number; provider: string; external_id: string; reason: string;
  detail: any; attempts: number; first_seen_at: string; last_seen_at: string;
};
export type UnmappedClient = { cliente: string; n: number; first_seen_at: string; last_seen_at: string; mapped_store_ids: number[] };
type Store = { id: number; nombre_fantasia: string };

export const DECISION_LABEL: Record<string, string> = {
  IMPORTED: "Importados", LINKED: "Vinculados", SKIPPED_TERMINAL: "Ya cerrados en LightData",
  WAITING_OAUTH: "Esperando Mercado Libre", OAUTH_PENDING: "No entraron por Mercado Libre",
  STORE_UNMAPPED: "Cliente sin tienda", ERROR: "Con error",
};
export const REASON_LABEL: Record<string, string> = {
  STORE_UNMAPPED: "Cliente sin tienda", LEGACY_NOT_FOUND: "No está en LightData",
  OAUTH_PENDING: "No entró por Mercado Libre", REF_CONFLICT: "Choca con otro envío",
};
const REASON_HELP: Record<string, string> = {
  STORE_UNMAPPED: "El cliente de LightData no está asociado a ninguna tienda (o a más de una). Se arregla asociándolo; lo que ya se escaneó hay que volver a escanearlo.",
  LEGACY_NOT_FOUND: "Se escaneó un código que no aparece en la copia de LightData. Puede ser un envío cargado hace minutos (el poller de LightData todavía no lo trajo) o una etiqueta de otra mensajería. Se cierra solo cuando el envío entra.",
  OAUTH_PENDING: "La tienda tiene Mercado Libre conectado, pero el envío no entró por ahí a tiempo. Se cierra solo si después entra. Si se repite en la misma tienda, revisar su conexión con Mercado Libre.",
  REF_CONFLICT: "El envío de LightData apunta a un envío de Lynx distinto del que ya tenía asociado. Hay que revisarlo a mano; «Ignorar» deja de avisarlo aunque se repita.",
};
const REASONS = ["STORE_UNMAPPED", "LEGACY_NOT_FOUND", "OAUTH_PENDING", "REF_CONFLICT"];
const PAGE = 200;

/* ===== Estilos (mismo lenguaje que OperacionPage.tsx) ===== */
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const sectionStyle: React.CSSProperties = { ...smallMutedStyle, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "10px" };
const kpiStyle: React.CSSProperties = { fontSize: "30px", fontWeight: 800, lineHeight: 1.1, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" };
const WARN = "var(--warning, #d29922)";
const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: "8px 12px", borderRadius: "12px", cursor: "pointer", font: "inherit", fontSize: "13px",
  border: `1px solid ${active ? "var(--primary, #4f8cff)" : "var(--border)"}`, fontWeight: active ? 700 : 400,
  background: active ? "var(--primary-soft, rgba(79,140,255,0.12))" : "transparent", color: "var(--text-primary)",
});
const rowStyle: React.CSSProperties = { display: "flex", gap: "12px", alignItems: "center", padding: "10px 0", borderBottom: "1px solid var(--border)", flexWrap: "wrap" };
const selectStyle: React.CSSProperties = { background: "transparent", color: "var(--text-primary)", border: "1px solid var(--border)", borderRadius: "10px", padding: "6px 8px", font: "inherit", fontSize: "13px", maxWidth: "260px" };

// «hace 3 min» a partir de una fecha del servidor.
export function hace(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "nunca";
  const min = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  if (min < 1) return "recién";
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `hace ${h} h`;
  return `hace ${Math.floor(h / 24)} días`;
}

// «5 min» / «10 s».
export function duracion(seg: number): string {
  if (seg < 60) return `${seg} s`;
  const min = Math.round(seg / 60);
  return min < 120 ? `${min} min` : `${Math.round(min / 60)} h`;
}

// Una pasada se considera atrasada si no corrió en 3 intervalos (y nunca
// menos de 2 minutos, para no alarmar por un intervalo corto de pruebas).
const atrasoMin = (seg: number) => Math.max(2, (3 * seg) / 60);

// Alertas: lo que quiere decir que algo anda mal, no solo que hay trabajo.
// Lo del sincronizador solo cuenta si está prendido: apagado no reintenta.
export function alertas(s: Status, now = Date.now()): string[] {
  const out: string[] = [];
  const minutos = (iso: string | null | undefined) => (iso ? (now - new Date(iso).getTime()) / 60000 : Infinity);
  const dias = s.sync_lookback_days ?? 3;
  if (s.sync_mode !== "off") {
    const ult = s.state?.last_run_at;
    if (!ult) out.push("El sincronizador está prendido pero todavía no registró ninguna pasada. Si el servidor arrancó hace más de unos minutos, revisar el log.");
    else if (minutos(ult) > atrasoMin(s.sync_interval_seconds)) out.push(`El sincronizador no corre desde ${hace(ult, now)} (debería cada ${duracion(s.sync_interval_seconds)}).`);
    // Un Flex de tienda con Mercado Libre espera a propósito: el umbral la contempla.
    const umbral = Math.max(120, s.oauth_wait_seconds / 60 + 30);
    if (s.oldest_pending_at && minutos(s.oldest_pending_at) > umbral) out.push(`Hay envíos pendientes desde ${hace(s.oldest_pending_at, now)}: casi siempre es un cliente sin tienda.`);
    if (s.recent_errors) out.push(`${s.recent_errors} envíos dieron error en el sincronizador en las últimas 24 h (ver el log del servidor).`);
    if (s.stale_pending) out.push(`${s.stale_pending} envíos pendientes ya no se reintentan solos (no tuvieron movimiento en LightData en ${dias} días): hay que escanearlos o revisarlos.`);
  }
  if (s.scan_import === "on" || s.sync_mode === "on") {
    const p = s.status_pass;
    if (!p?.last_run_at) out.push("La copia de estados todavía no registró ninguna pasada. Si el servidor arrancó hace más de unos minutos, revisar el log.");
    else if (minutos(p.last_run_at) > atrasoMin(s.status_interval_seconds)) out.push(`La copia de estados no corre desde ${hace(p.last_run_at, now)} (debería cada ${duracion(s.status_interval_seconds)}).`);
    if (p?.last_result?.error) out.push(`La última copia de estados falló: ${p.last_result.error}`);
    else if (p?.last_result?.errors) out.push(`La última copia de estados tuvo ${p.last_result.errors} envíos con error (ver el log del servidor).`);
  }
  return out;
}

// Qué ofrecer para un cliente sin tienda, según su mapeo VIGENTE.
export function accionCliente(c: UnmappedClient): "sin-nombre" | "ambiguo" | "asociado" | "asociar" {
  if (!c.cliente) return "sin-nombre";
  if (c.mapped_store_ids.length > 1) return "ambiguo";
  if (c.mapped_store_ids.length === 1) return "asociado";
  return "asociar";
}

function mensajeError(e: any): string {
  // fetch sin red tira TypeError («Failed to fetch»): se dice en castellano.
  if (e instanceof TypeError) return "Sin conexión con el servidor. Se muestran los datos de la última carga.";
  return e?.message || "Error desconocido";
}

async function leer(res: Response) {
  const j = await res.json().catch(() => ({}));
  return j ?? {};
}

export default function LightdataMonitorPage() {
  const { token, financeAccess } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState<Status | null>(null);
  const [clientes, setClientes] = useState<UnmappedClient[]>([]);
  const [lista, setLista] = useState<Exception[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [schemaMissing, setSchemaMissing] = useState(false);
  const [actualizado, setActualizado] = useState<Date | null>(null);
  const [tab, setTab] = useState("STORE_UNMAPPED");
  const [eleccion, setEleccion] = useState<Record<string, string>>({});
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  // «Actualizar», el intervalo y el cambio de pestaña pueden pisarse: solo
  // se aplica la respuesta del pedido más reciente.
  const pedido = useRef(0);

  const cargar = useCallback(async () => {
    const n = ++pedido.current;
    try {
      const detalle = tab === "STORE_UNMAPPED"
        ? "/v2/integrations/lightdata/unmapped-clients"
        : `/v2/integrations/exceptions?reason=${tab}&limit=${PAGE}`;
      const [rs, rd] = await Promise.all([
        authFetch("/v2/integrations/lightdata/status", { token }),
        authFetch(detalle, { token }),
      ]);
      const js = await leer(rs);
      const jd = await leer(rd);
      if (n !== pedido.current) return;
      if (js.code === "SCHEMA_MISSING" || jd.code === "SCHEMA_MISSING") {
        setSchemaMissing(true); setS(null); setError(null); setActualizado(new Date());
        return;
      }
      if (!rs.ok || js.ok === false) throw new Error(js.error || `Error ${rs.status}`);
      if (!rd.ok || jd.ok === false) throw new Error(jd.error || `Error ${rd.status}`);
      setSchemaMissing(false);
      setS(js);
      if (tab === "STORE_UNMAPPED") setClientes(jd.clients ?? []);
      else setLista((jd.exceptions ?? []).filter((e: Exception) => e.provider === "lightdata" || e.provider === "mercadolibre"));
      setError(null); setActualizado(new Date());
    } catch (e: any) {
      if (n === pedido.current) setError(mensajeError(e));
    }
  }, [token, tab]);

  useEffect(() => { cargar(); const t = window.setInterval(cargar, 60_000); return () => window.clearInterval(t); }, [cargar]);

  // Las tiendas, para nombrarlas y para el selector de «Asociar».
  useEffect(() => {
    authFetch("/v2/stores", { token })
      .then(async (res) => { const j = await leer(res); if (res.ok && j.ok !== false) setStores(j.stores ?? []); })
      .catch(() => {});
  }, [token]);

  const storeName = useCallback((id: any) => stores.find((x) => Number(x.id) === Number(id))?.nombre_fantasia ?? `Tienda ${id}`, [stores]);
  const storesOrdenadas = useMemo(() => [...stores].sort((a, b) => String(a.nombre_fantasia).localeCompare(String(b.nombre_fantasia))), [stores]);
  const lasAlertas = useMemo(() => (s ? alertas(s) : []), [s]);
  const abiertas = (r: string) => s?.open_exceptions?.[r] ?? 0;
  const totalAbiertas = REASONS.reduce((a, r) => a + abiertas(r), 0);
  const dias = s?.sync_lookback_days ?? 3;

  // Qué pasa con lo pendiente después de asociar: depende del sincronizador.
  const despuesDeAsociar = s?.sync_mode === "on"
    ? `El sincronizador trae solo los que tuvieron movimiento en LightData en los últimos ${dias} días; lo que ya se escaneó hay que volver a escanearlo.`
    : "Lo pendiente hay que volver a escanearlo (el sincronizador no está trayendo envíos).";

  const asociar = async (cliente: string) => {
    const storeId = Number(eleccion[cliente]);
    if (!storeId) return;
    setOcupado(cliente); setAviso(null);
    try {
      const res = await authFetch("/v1/liquidaciones/config/store-map", { method: "POST", token, json: true, body: JSON.stringify({ legacyNombreFantasia: cliente, storeId }) });
      const j = await leer(res);
      if (res.status === 409) setAviso(`«${cliente}» ya estaba asociado a una tienda (puede que alguien lo haya hecho recién). Se actualizó la lista.`);
      else if (!res.ok || j.ok === false) throw new Error(j.error || `Error ${res.status}`);
      else setAviso(`«${cliente}» quedó asociado a ${storeName(storeId)}. ${despuesDeAsociar}`);
      await cargar();
    } catch (e: any) { setAviso(`No se pudo asociar «${cliente}»: ${mensajeError(e)}`); }
    finally { setOcupado(null); }
  };

  const cerrarCliente = async (cliente: string, status: "RESOLVED" | "IGNORED") => {
    setOcupado(`c:${cliente}`); setAviso(null);
    try {
      const res = await authFetch("/v2/integrations/lightdata/unmapped-clients", { method: "PUT", token, json: true, body: JSON.stringify({ cliente, status }) });
      const j = await leer(res);
      if (!res.ok || j.ok === false) throw new Error(j.error || `Error ${res.status}`);
      await cargar();
    } catch (e: any) { setAviso(`No se pudo actualizar: ${mensajeError(e)}`); }
    finally { setOcupado(null); }
  };

  const cerrar = async (e: Exception, status: "RESOLVED" | "IGNORED") => {
    setOcupado(String(e.id)); setAviso(null);
    try {
      const res = await authFetch(`/v2/integrations/exceptions/${e.id}`, { method: "PUT", token, json: true, body: JSON.stringify({ status }) });
      const j = await leer(res);
      if (!res.ok || j.ok === false) throw new Error(j.error || `Error ${res.status}`);
      await cargar();
    } catch (err: any) { setAviso(`No se pudo actualizar: ${mensajeError(err)}`); }
    finally { setOcupado(null); }
  };

  const describir = (e: Exception) => {
    const d = e.detail ?? {};
    if (e.reason === "LEGACY_NOT_FOUND") return e.provider === "lightdata" ? `Etiqueta de LightData n.º ${e.external_id}` : `QR de Mercado Libre ${d.tracking ?? e.external_id}`;
    if (e.reason === "OAUTH_PENDING") return `Envío de ML ${d.tracking ?? "—"} · ${d.store_id ? storeName(d.store_id) : "tienda desconocida"} (LightData n.º ${e.external_id})`;
    if (e.reason === "REF_CONFLICT") return `LightData n.º ${e.external_id} · choca con ${JSON.stringify(d.conflicts ?? d)}`;
    return `${e.provider} ${e.external_id}`;
  };

  const encendido = (on: boolean) => <b style={{ color: on ? "var(--success)" : "var(--text-secondary)" }}>{on ? "prendido" : "apagado"}</b>;
  // La marca de ensayo sale del estado REAL del sincronizador (lo que contó),
  // no del .env: recién cambiado, el job todavía no corrió en el modo nuevo.
  const enEnsayo = s?.state?.mode === "shadow";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>LightData → Current</h2>
            <p style={subtitleStyle}>Los envíos de tiendas sin Mercado Libre conectado que se traen de LightData: al escanearlos en el depósito o antes, cuando LightData los marca colectados.</p>
          </div>
          <Button variant="secondary" onClick={cargar}>Actualizar</Button>
        </div>
        <div style={{ ...smallMutedStyle, marginTop: "10px" }}>
          {actualizado ? `Actualizado ${actualizado.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })} · cada 1 min` : "Cargando…"}
          {error ? <span role="alert" style={{ color: "var(--danger)", marginLeft: "12px" }}>{error}</span> : null}
        </div>
      </Card>

      {schemaMissing ? (
        <Card style={{ borderColor: "var(--danger)" }}>
          <div role="alert">
            <div style={{ ...sectionStyle, color: "var(--danger)" }}>Falta la base</div>
            <div style={{ color: "var(--text-primary)", lineHeight: 1.6 }}>
              El código está instalado pero faltan sus tablas: hay que correr <code>server/migration</code> en el servidor.
              Mientras tanto, si el alta al escanear está prendida, escanear una etiqueta de LightData en el depósito da error.
            </div>
          </div>
        </Card>
      ) : null}

      {s && lasAlertas.length ? (
        <Card style={{ borderColor: WARN }}>
          <div role="status" aria-live="polite">
            <div style={{ ...sectionStyle, color: WARN }}>Para revisar</div>
            <ul style={{ margin: 0, paddingLeft: "18px", color: "var(--text-primary)", lineHeight: 1.7 }}>
              {lasAlertas.map((a) => <li key={a}>{a}</li>)}
            </ul>
          </div>
        </Card>
      ) : null}

      {s ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" }}>
          <Card>
            <div style={sectionStyle}>Cómo está configurado</div>
            <div style={{ display: "grid", gap: "8px", fontSize: "14px", color: "var(--text-primary)" }}>
              <div>Alta al escanear: {encendido(s.scan_import === "on")}</div>
              <div>
                Traer antes de que llegue: {s.sync_mode === "shadow" ? <b style={{ color: WARN }}>en ensayo (no crea nada)</b> : encendido(s.sync_mode === "on")}
                {s.state && s.sync_mode !== "off" ? <span style={smallMutedStyle}> · desde {new Date(s.state.since).toLocaleString("es-AR")}</span> : null}
              </div>
              <div style={smallMutedStyle}>Espera a Mercado Libre: {duracion(s.oauth_wait_seconds)} · se cambia en el .env del servidor.</div>
            </div>
            <div style={{ ...sectionStyle, marginTop: "16px" }}>Últimas pasadas</div>
            <div style={{ display: "grid", gap: "6px", fontSize: "13px", color: "var(--text-primary)" }}>
              <div>
                Sincronizador: {s.sync_mode === "off" ? "—" : hace(s.state?.last_run_at)}
                {s.sync_mode !== "off" && s.state?.last_result?.seen !== undefined ? <span style={smallMutedStyle}> · miró {s.state.last_result.seen}</span> : null}
              </div>
              <div>
                Copia de estados: {hace(s.status_pass?.last_run_at)}
                {s.status_pass?.last_result && !s.status_pass.last_result.error ? (
                  <span style={smallMutedStyle}> · {s.status_pass.last_result.changed ?? 0} cambiados, {s.status_pass.last_result.closed ?? 0} cerrados{s.status_pass.last_result.settled ? `, ${s.status_pass.last_result.settled} avisos resueltos solos` : ""}</span>
                ) : null}
              </div>
            </div>
          </Card>

          <Card>
            <div style={sectionStyle}>Entraron en las últimas 24 h</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" }}>
              <div><div style={kpiStyle}>{(s.imported_24h?.scan ?? 0).toLocaleString("es-AR")}</div><div style={smallMutedStyle}>al escanear</div></div>
              <div><div style={kpiStyle}>{(s.imported_24h?.sync ?? 0).toLocaleString("es-AR")}</div><div style={smallMutedStyle}>antes de llegar</div></div>
            </div>
            {s.sync_mode !== "off" && s.state ? (
              <>
                <div style={{ ...sectionStyle, marginTop: "16px" }}>Sincronizador{enEnsayo ? " (ensayo)" : ""} · desde {new Date(s.state.since).toLocaleDateString("es-AR")}</div>
                <div style={{ display: "grid", gap: "4px", fontSize: "13px" }}>
                  {Object.keys(DECISION_LABEL).filter((k) => s.totals[k]).map((k) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", color: ["STORE_UNMAPPED", "OAUTH_PENDING", "ERROR"].includes(k) ? WARN : "var(--text-primary)" }}>
                      <span>{DECISION_LABEL[k]}</span><b style={{ fontVariantNumeric: "tabular-nums" }}>{s.totals[k].toLocaleString("es-AR")}</b>
                    </div>
                  ))}
                  {Object.keys(s.totals).length === 0 ? <span style={smallMutedStyle}>Todavía nada.</span> : null}
                </div>
              </>
            ) : null}
          </Card>
        </div>
      ) : null}

      {s ? (
        <Card>
          <div style={sectionStyle}>Bandeja · {totalAbiertas.toLocaleString("es-AR")} abiertos</div>
          <div role="tablist" aria-label="Motivos" style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
            {REASONS.map((r) => (
              <button key={r} type="button" role="tab" aria-selected={tab === r} style={tabStyle(tab === r)} onClick={() => { setTab(r); setAviso(null); }}>
                {REASON_LABEL[r]} <b style={{ color: abiertas(r) ? WARN : "var(--text-secondary)" }}>{abiertas(r).toLocaleString("es-AR")}</b>
              </button>
            ))}
          </div>
          <div style={{ ...smallMutedStyle, marginBottom: "8px", lineHeight: 1.5 }}>{REASON_HELP[tab]}</div>
          {aviso ? <div role="status" style={{ fontSize: "13px", color: "var(--text-primary)", margin: "8px 0" }}>{aviso}</div> : null}

          {tab === "STORE_UNMAPPED" ? (
            clientes.length === 0 ? <div style={smallMutedStyle}>Nada pendiente.</div> : clientes.map((c) => {
              const accion = accionCliente(c);
              const clave = c.cliente || "(sin nombre)";
              return (
                <div key={clave} style={rowStyle}>
                  <div style={{ flex: "1 1 240px" }}>
                    <b style={{ color: "var(--text-primary)" }}>{c.cliente || "(sin nombre en LightData)"}</b>
                    <div style={smallMutedStyle}>
                      {c.n.toLocaleString("es-AR")} {c.n === 1 ? "envío" : "envíos"} · último {hace(c.last_seen_at)}
                      {accion === "asociado" ? <span style={{ color: "var(--success)" }}> · ya asociado a {storeName(c.mapped_store_ids[0])}: falta volver a escanear lo pendiente</span> : null}
                      {accion === "ambiguo" ? <span style={{ color: WARN }}> · asociado a varias tiendas: {c.mapped_store_ids.map(storeName).join(", ")} (dejar una sola en Tiendas → Legacy)</span> : null}
                      {accion === "sin-nombre" ? <span style={{ color: WARN }}> · LightData no informa el cliente: revisar el envío en LightData</span> : null}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                    {accion === "ambiguo" ? <Button variant="secondary" onClick={() => navigate("/admin/stores")}>Ir a Tiendas</Button> : null}
                    {accion === "asociar" && financeAccess ? (
                      <>
                        <select aria-label={`Tienda para ${c.cliente}`} style={selectStyle} value={eleccion[c.cliente] ?? ""} onChange={(e) => setEleccion((p) => ({ ...p, [c.cliente]: e.target.value }))}>
                          <option value="">Elegir tienda…</option>
                          {storesOrdenadas.map((x) => <option key={x.id} value={x.id}>{x.nombre_fantasia}</option>)}
                        </select>
                        <Button variant="primary" disabled={!eleccion[c.cliente] || ocupado === c.cliente} onClick={() => asociar(c.cliente)}>Asociar</Button>
                      </>
                    ) : null}
                    {accion === "asociar" && !financeAccess ? <span style={smallMutedStyle}>Lo asocia alguien con acceso a Liquidaciones (Tiendas → Legacy).</span> : null}
                    {accion === "asociado" ? <Button variant="secondary" disabled={ocupado === `c:${c.cliente}`} onClick={() => cerrarCliente(c.cliente, "RESOLVED")}>Resuelto</Button> : null}
                    <Button variant="ghost" disabled={ocupado === `c:${c.cliente}`} onClick={() => cerrarCliente(c.cliente, "IGNORED")}>Ignorar</Button>
                  </div>
                </div>
              );
            })
          ) : lista.length === 0 ? <div style={smallMutedStyle}>Nada pendiente.</div> : (
            <>
              {lista.map((e) => (
                <div key={e.id} style={rowStyle}>
                  <div style={{ flex: "1 1 260px" }}>
                    <div style={{ color: "var(--text-primary)", fontSize: "14px" }}>{describir(e)}</div>
                    <div style={smallMutedStyle}>{e.attempts} {e.attempts === 1 ? "vez" : "veces"} · primera {hace(e.first_seen_at)} · última {hace(e.last_seen_at)}</div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Button variant="secondary" disabled={ocupado === String(e.id)} onClick={() => cerrar(e, "RESOLVED")}>Resuelto</Button>
                    <Button variant="ghost" disabled={ocupado === String(e.id)} onClick={() => cerrar(e, "IGNORED")}>Ignorar</Button>
                  </div>
                </div>
              ))}
              {abiertas(tab) > lista.length ? <div style={{ ...smallMutedStyle, marginTop: "8px" }}>Mostrando los {lista.length} más recientes de {abiertas(tab).toLocaleString("es-AR")}.</div> : null}
            </>
          )}
        </Card>
      ) : null}

      {!schemaMissing ? <LightdataReconciliation token={token} /> : null}
    </div>
  );
}
